/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { FunctionComponent, MutableRefObject, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import { isEqual } from 'lodash-es'
import { css } from 'styled-components'
import styled from 'styled-components/native'

import { FieldSchema } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Note, NoteStatusStates } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { HeaderText } from '../../atoms/headerText/HeaderText'
import { Size as HeaderTextSize } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { FormBody, FormBodyCustomStyles } from '../formBody/FormBody'
import { UICoreFormContext } from '../formBody/types'

export type NoteContainerProps = {
  note: Note
  saveForm: ({ values, formSubmit }: { values: Dict; formSubmit: boolean }) => void
  formContext?: UICoreFormContext
  /** Forces the form read-only even when the note is not completed (e.g. a lockout window has closed). */
  readOnly?: boolean
  noteRef?: MutableRefObject<any>
  onFormReady?: () => void
  providerName?: string
  onNoteValuesChange?: (values: Dict) => void
  /**
   * Names of the form fields whose value changes should reinitialize the form to its own current
   * values, so derived schema content (e.g. the DA Flexible Note session rate, recomputed from the
   * selected modality) re-renders live instead of only after the next autosave. The reinitialize
   * snapshots ALL current values, so in-progress input in other fields is preserved across it.
   *
   * Only these fields trigger the (relatively expensive) full-form reinitialize — typing in any other
   * field doesn't churn the form. Leave empty/undefined to disable: static notes keep frozen values.
   */
  reinitializeOnFieldsChange?: string[]
}

const Header = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  paddingLeft: theme.spacing['16px'],
  paddingTop: '0px',
  paddingBottom: '0px',
  borderBottomWidth: '0px',
  ...(IS_WEB && { borderBottomStyle: 'solid' }),
  borderBottomColor: `${theme.colors.borderDefault}`,
  borderTopLeftRadius: theme.spacing['16px'],
  borderTopRightRadius: theme.spacing['16px'],
}))

const FormContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  height: '100%',
  borderWidth: '0px',
  borderColor: theme.colors.borderDefault,
  borderRadius: theme.spacing['16px'],
}))

export const NoteContainer: FunctionComponent<NoteContainerProps> = ({
  note,
  saveForm,
  formContext,
  readOnly: readOnlyOverride,
  noteRef,
  onFormReady,
  providerName,
  onNoteValuesChange,
  reinitializeOnFieldsChange,
}) => {
  const intl = useIntl()

  const readOnly = readOnlyOverride || note?.status === NoteStatusStates.COMPLETED
  const initialNote = useRef<Note>(note)
  const updatedNoteValues = useRef<Dict>({})

  const declarationProviderName = useMemo(() => {
    if (note?.status === NoteStatusStates.COMPLETED) {
      return note?.body?.declarationProviderName || note?.body?.approverName || note?.body?.authorName || ''
    } else {
      return providerName
    }
  }, [note, providerName])

  // Seed/base form values, taken once from the frozen `initialNote` (see note below on why frozen).
  const baseInitialValues = useMemo(
    () => ({
      ...initialNote.current?.content_meta_data?.meta_data?.initialValues,
      ...initialNote.current,
      ...initialNote.current?.body,
      declarationProviderName,
    }),
    // `initialNote` is a frozen ref; only rebuild if the declaration/signature name resolves.
    [declarationProviderName],
  )

  const reinitializeOnValueChange = !!reinitializeOnFieldsChange?.length
  const [trackedValues, setTrackedValues] = useState<Dict | null>(null)
  const initialValues = useMemo(
    () => (reinitializeOnValueChange && trackedValues ? { ...baseInitialValues, ...trackedValues } : baseInitialValues),
    [reinitializeOnValueChange, trackedValues, baseInitialValues],
  )

  const schemaSource = reinitializeOnValueChange ? note : initialNote.current

  return (
    <>
      <FormContainer testID={tID('NoteContainer')}>
        <Header>
          <View testID={tID('NoteContainer-title')}>
            <HeaderText text={note?.content_meta_data?.title} size={HeaderTextSize.LARGE} />
          </View>
        </Header>
        <FormBody
          intl={intl}
          readOnly={readOnly}
          formContext={{ useMultiSelectTypeAhead: true, saveSingleSelectFieldsAsArray: true, ...formContext }}
          schema={schemaSource?.content_meta_data?.meta_data?.schema as FieldSchema}
          uiSchema={schemaSource?.content_meta_data?.meta_data?.uiSchema}
          name={schemaSource?.content_meta_data?.name}
          initialValues={initialValues}
          formButton={undefined}
          saveForm={saveForm}
          onFormChange={({ values, formSubmit }: { values: Dict; formSubmit: boolean }) => {
            saveForm({ values, formSubmit })
            updatedNoteValues.current = values
            onNoteValuesChange?.(values)
            if (reinitializeOnValueChange) {
              setTrackedValues((prev) =>
                prev && reinitializeOnFieldsChange!.every((field) => isEqual(prev[field], values[field]))
                  ? prev
                  : values,
              )
            }
          }}
          withPageBreaks={false}
          formBodyCustomStyles={
            {
              submitButtonContainer: {
                borderTopWidth: 0,
                padding: 0,
                width: '100%',
                boxShadow: 'none',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px',
              },
              submitButtonWrapper: { maxWidth: 'none', marginLeft: '40px', marginBottom: '20px' },
            } as unknown as FormBodyCustomStyles
          }
          scrollContainerCustomStyles={{
            formBodyPageContainer: css`
              max-width: none;
            `,
          }}
          formRef={noteRef}
          onFormReady={onFormReady}
        />
      </FormContainer>
    </>
  )
}
